import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Alert, Box, Button, Group, Stack, Text, Title } from '@mantine/core'
import { useNavigate } from 'react-router-dom'
import { switchOrg } from '../services/actions/userActions'
import { loadUserContext } from '../services/utils/loadUserContext'
import { logout } from '../services/actions/authActions'
import { setCurrentExperience } from '../services/store/userSlice'

const DeactivatedOrgPage = () => {
	const dispatch = useDispatch()
	const navigate = useNavigate()
	const currentOrg = useSelector((state) => state.user.currentOrg)
	const orgMemberships = useSelector((state) => state.user.orgMemberships || [])
	const [switchingOrgSlug, setSwitchingOrgSlug] = useState('')
	const [error, setError] = useState('')

	const activeOrgOptions = useMemo(
		() =>
			orgMemberships.filter(
				(org) =>
					org.isActive !== false && org.orgSlug !== currentOrg?.orgSlug
			),
		[orgMemberships, currentOrg?.orgSlug]
	)

	const handleSwitchOrg = async (org) => {
		try {
			setError('')
			setSwitchingOrgSlug(org.orgSlug)
			await switchOrg(org.orgSlug)
			dispatch(setCurrentExperience(null))
			await loadUserContext(dispatch)
			navigate('/')
		} catch (err) {
			console.error('Failed to switch organization:', err)
			setError(err.message || 'Failed to switch organization.')
		} finally {
			setSwitchingOrgSlug('')
		}
	}

	return (
		<Box px='md' py='xl' maw={760}>
			<Title order={2} mb='sm'>
				Organization Access Deactivated
			</Title>
			<Text c='dimmed' mb='lg'>
				Your access to {currentOrg?.orgName || 'this organization'} has been
				deactivated. Contact an organization admin if you need access restored.
			</Text>

			{error && (
				<Alert color='red' mb='md'>
					{error}
				</Alert>
			)}

			{activeOrgOptions.length > 0 ? (
				<>
					<Text fw={600} mb='sm'>
						Switch to another organization
					</Text>
					<Stack spacing='sm' maw={420}>
						{activeOrgOptions.map((org) => (
							<Button
								key={org.orgId}
								variant='light'
								fullWidth
								loading={switchingOrgSlug === org.orgSlug}
								onClick={() => handleSwitchOrg(org)}
							>
								{org.orgName}
							</Button>
						))}
					</Stack>
				</>
			) : (
				<Text mb='md'>
					You do not currently have access to another active organization.
				</Text>
			)}

			<Group mt='xl'>
				<Button variant='subtle' color='gray' onClick={logout}>
					Log Out
				</Button>
			</Group>
		</Box>
	)
}

export default DeactivatedOrgPage
